<template>
  <v-container fluid class="pa-0">
    <!-- Filter Component -->
    <SalesFilter />

    <v-empty-state
      icon="mdi-calendar"
      class="w-100 align-center justify-center"
      v-if="!hasData && !loading"
    >
      <template v-slot:headline>
        <div class="text-h4">No sales data found</div>
      </template>
    </v-empty-state>

    <v-row>
      <v-col cols="12">
        <!-- <v-card> -->
        <!--   <v-card-text> -->
        <v-alert v-if="error" type="error" class="my-4 mx-4">
          {{ error }}
        </v-alert>

        <v-alert v-if="!salesData && !loading" type="info" class="my-4 mx-4">
          No daily sales data available. Start by submitting sales records.
        </v-alert>

        <div v-if="loading" class="text-center">
          <v-progress-circular indeterminate></v-progress-circular>
          <div class="mt-2 mx-4">Loading daily sales data...</div>
        </div>

        <div v-else-if="salesData && hasData">
          <v-alert v-if="exporting" type="info" density="compact" class="my-4 mx-4">
            <v-progress-linear indeterminate class="mt-2 mx-4"></v-progress-linear>
            <div class="text-caption mt-1">Exporting to CSV...</div>
          </v-alert>

          <v-alert v-if="hasActiveFilters" type="info" density="compact" class="my-4 mx-4" closable>
            <div v-if="totalsSummary">
              <span class="mr-3"
                >Total amount: <b>{{ formatCurrency(totalsSummary.amount) }}</b></span
              >
              <span class="mr-3"
                >Average per day: <b>{{ formatCurrency(totalsSummary.amount_avg) }}</b> ({{
                  totalsSummary.number_of_days
                }}
                days)</span
              >
              <span class="mr-3"
                >Orders: <b>{{ totalsSummary.orders }}</b>
                <span class="text-disabled"> (excluded: {{ totalsSummary.excludedOrders }})</span>
              </span>
              <!-- <span class="mr-3" -->
              <!--   >Order average: -->
              <!--   <b>{{ -->
              <!--     formatAmount( -->
              <!--       totalsSummary.amount / (totalsSummary.orders - totalsSummary.excludedOrders), -->
              <!--       0, -->
              <!--       0, -->
              <!--     ) -->
              <!--   }}</b> -->
              <!--   AED</span -->
              <!-- > -->
              <span
                >Records: <b>{{ totalsSummary.records }}</b></span
              >
              <!-- <div class="text-caption">{{ filterSummary }}</div> -->
            </div>

            <template v-if="exporting">
              <v-progress-linear indeterminate class="mt-2 mx-4"></v-progress-linear>
              <div class="text-caption mt-1">Exporting to CSV...</div>
            </template>
          </v-alert>
          <!-- Date Range Summary -->
          <!-- <v-alert v-if="dateRangeSummary" type="info" density="compact" class="my-4 mx-4" closable> -->
          <!--   {{ dateRangeSummary }} {{ filterSummary }} -->
          <!-- </v-alert> -->

          <!-- Simple Vuetify table without complex styling -->
          <v-table class="sticky-table">
            <thead>
              <!--------->
              <tr>
                <th>
                  <v-btn
                    icon="mdi-arrow-expand-vertical"
                    @click="expandAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                  <v-btn
                    icon="mdi-arrow-collapse-vertical"
                    @click="collapseAll"
                    :disabled="!hasData"
                    variant="plain"
                  ></v-btn>
                </th>
                <th v-for="day in salesData.days" :key="day" colspan="1" class="text-center">
                  <span v-html="formatDayForTable(day)"></span>
                </th>
                <th class="text-center">Total</th>
                <th class="text-center">Avg/Day</th>
              </tr>
            </thead>

            <tbody>
              <template v-for="city in getCitiesWithData()" :key="city">
                <!-- City Header Row -->
                <tr @click="toggleCity(city)">
                  <td class="text-left pl-2">
                    <v-icon small class="mr-1">
                      {{ isCityExpanded(city) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                    <strong :class="cityColor(city)">{{ city }} </strong>
                  </td>

                  <td
                    v-for="day in safeArray(salesData.days)"
                    :key="day"
                    class="text-right"
                    :class="cityColor(city)"
                  >
                    <strong>
                      {{ formatAmount(getSafeValue(salesData.rowTotals, [city, day], 0)) }}</strong
                    >
                  </td>
                  <td class="text-right text-disabled">
                    <strong>{{ formatAmount(getCityTotal(city)) }}</strong>
                  </td>
                  <td class="text-right text-disabled">
                    <strong>{{ formatAmount(getCityAverage(city)) }}</strong>
                  </td>
                </tr>

                <!-- Locations for this city -->
                <template v-if="isCityExpanded(city)">
                  <template v-for="location in getLocationsWithData(city)" :key="location">
                    <!-- Location Total Row -->
                    <tr @click="toggleLocation(city, location)">
                      <td class="text-left" style="padding-left: 1rem">
                        <v-icon small class="mr-1">
                          {{
                            isLocationExpanded(city, location)
                              ? "mdi-chevron-down"
                              : "mdi-chevron-right"
                          }}
                        </v-icon>
                        <span :class="locationColor(location)">{{ location }}</span>
                      </td>
                      <td
                        v-for="day in safeArray(salesData.days)"
                        :key="day"
                        class="text-right"
                        :class="locationColor(location)"
                      >
                        {{ formatAmount(getLocationCityDayTotal(city, location, day)) }}
                      </td>
                      <td class="text-right text-disabled">
                        <strong>{{ formatAmount(getLocationCityTotal(city, location)) }}</strong>
                      </td>
                      <td class="text-right text-disabled">
                        <strong>{{ formatAmount(getLocationCityAverage(city, location)) }}</strong>
                      </td>
                    </tr>

                    <!-- Brands for this location -->
                    <template v-if="isLocationExpanded(city, location)">
                      <template v-for="brand in getBrandsWithData(city, location)" :key="brand">
                        <!-- Brand Total Row -->
                        <tr @click="toggleBrand(city, location, brand)">
                          <td class="text-left" style="padding-left: 2rem">
                            <v-icon small class="mr-1">
                              {{
                                isBrandExpanded(city, location, brand)
                                  ? "mdi-chevron-down"
                                  : "mdi-chevron-right"
                              }}
                            </v-icon>
                            <span :class="brandColor(brand)">{{ brand }}</span>
                          </td>
                          <td
                            v-for="day in safeArray(salesData.days)"
                            :key="day"
                            class="text-right"
                            :class="brandColor(brand)"
                          >
                            {{
                              formatAmount(getBrandLocationCityDayTotal(city, location, brand, day))
                            }}
                          </td>

                          <td class="text-right text-disabled">
                            <strong>{{
                              formatAmount(getBrandLocationCityTotal(city, location, brand))
                            }}</strong>
                          </td>

                          <td class="text-right text-disabled">
                            <strong>
                              {{ formatAmount(getBrandLocationCityAverage(city, location, brand)) }}
                            </strong>
                          </td>
                        </tr>

                        <!-- Platforms for this brand -->
                        <template v-if="isBrandExpanded(city, location, brand)">
                          <tr
                            v-for="platform in getPlatformsWithData(city, location, brand)"
                            :key="platform"
                          >
                            <td class="text-left">
                              <span style="padding-left: 3rem">{{ platform }}</span>
                            </td>
                            <td
                              v-for="day in safeArray(salesData.days)"
                              :key="day"
                              class="text-right"
                            >
                              {{
                                formatAmount(getPlatformData(city, location, brand, platform, day))
                              }}
                            </td>
                            <td class="text-right text-disabled">
                              {{
                                formatAmount(
                                  getPlatformTotalForBrand(city, location, brand, platform),
                                )
                              }}
                            </td>
                            <td class="text-right text-disabled">
                              {{
                                formatAmount(
                                  getPlatformAverageForBrand(city, location, brand, platform),
                                )
                              }}
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>

            <!-- Grand Totals Footer -->
            <tfoot>
              <tr>
                <td class="text-left"><strong>Total</strong></td>
                <td v-for="day in safeArray(salesData.days)" :key="day" class="text-right">
                  <strong>{{ formatAmount(getGrandDayTotal(day)) }}</strong>
                </td>
                <td class="text-right text-disabled">
                  <strong>{{ formatAmount(getSafeValue(salesData, ["grandTotal"], 0)) }}</strong>
                </td>
                <td class="text-right text-disabled">
                  <strong>{{ formatAmount(getGrandAverage()) }}</strong>
                </td>
              </tr>
            </tfoot>
          </v-table>

          <!-- Summary Section -->
          <!-- <v-row class="mt-6" v-if="isDev && hasSummaryData"> -->
          <!--   <v-col cols="12" md="6"> -->
          <!--     <v-card class="summary-card" variant="outlined"> -->
          <!--       <v-card-title class="text-h6">Platform Performance</v-card-title> -->
          <!--       <v-card-text> -->
          <!--         <v-row> -->
          <!--           <v-col v-for="platform in getPlatformsWithAnyData()" :key="platform" cols="6"> -->
          <!--             <div class="platform-summary"> -->
          <!--               <div class="text-body-2 text-medium-emphasis">{{ platform }}</div> -->
          <!--               <div class="text-h6 text-primary font-weight-bold"> -->
          <!--                 {{ formatAmount(getPlatformTotal(platform)) }} -->
          <!--               </div> -->
          <!--               <div class="text-caption text-medium-emphasis"> -->
          <!--                 Avg: {{ formatAmount(getPlatformAverage(platform)) }}/day -->
          <!--               </div> -->
          <!--             </div> -->
          <!--           </v-col> -->
          <!--         </v-row> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!--   <v-col cols="12" md="6"> -->
          <!--     <v-card class="summary-card" variant="outlined"> -->
          <!--       <v-card-title class="text-h6">Location Performance</v-card-title> -->
          <!--       <v-card-text> -->
          <!--         <v-row> -->
          <!--           <v-col v-for="location in getLocationsWithAnyData()" :key="location" cols="6"> -->
          <!--             <div class="location-summary"> -->
          <!--               <div class="text-body-2 text-medium-emphasis">{{ location }}</div> -->
          <!--               <div class="text-h6 text-primary font-weight-bold"> -->
          <!--                 {{ formatAmount(getLocationTotal(location)) }} -->
          <!--               </div> -->
          <!--               <div class="text-caption text-medium-emphasis"> -->
          <!--                 Avg: {{ formatAmount(getLocationAverage(location)) }}/day -->
          <!--               </div> -->
          <!--             </div> -->
          <!--           </v-col> -->
          <!--         </v-row> -->
          <!--       </v-card-text> -->
          <!--     </v-card> -->
          <!--   </v-col> -->
          <!-- </v-row> -->

          <!-- NET sales note -->
          <div class="text-caption text-disabled text-right mt-2 mr-4">
            Sales are NET from January 2026 onwards
          </div>
        </div>
        <!-- </v-card-text> -->
        <!-- </v-card> -->
      </v-col>
    </v-row>

  </v-container>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, reactive, computed, watch, watchEffect } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/api.js";
import SalesFilter from "@/components/SalesFilter.vue";
import { useFilter } from "@/composables/useFilter.js";
import { useApp } from "@/composables/useApp.js";
import { useSalesForm } from "@/composables/useSalesForm";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import { useAppBarActions } from "@/composables/useAppBarActions.js";

const { isDev } = useApp();

const router = useRouter();
const { setActions, clearActions } = useAppBarActions();

// Use the composable
const {
  formatCurrency,
  formatAmount,
  activeFilters,
  toggleFilterExpand,
  isFilterExpanded,
  shallowEqual,
  // filterSummary,
  totalsSummary,
  filterTotals,
  hasActiveFilters,
  numberOfActiveFilters,
} = useFilter();

const { cityColor, locationColor, brandColor } = useSalesForm();

// Confirm dialog
const { confirm } = useConfirmDialog();

// Reactive state
const salesData = ref(null);
const loading = ref(false);
const error = ref("");
const exporting = ref(false);

// Track expanded/collapsed state
const expandedCities = reactive({});
const expandedLocations = reactive({});
const expandedBrands = reactive({});

// Watchers and lifecycle
watch(activeFilters, async (val, oldVal) => {
  if (!shallowEqual(val, oldVal)) {
    await fetchDailyData();
  }
});

onMounted(async () => {
  try {
    // await fetchOptions();
    // clearFilters();
    // await applyDatePreset("7d");
    // applyFilters();
    await fetchDailyData();
  } catch (err) {
    console.error("Failed to initialize:", err);
  }
});

// Computed properties
const hasData = computed(() => {
  return (
    salesData.value &&
    safeArray(salesData.value.cities).length > 0 &&
    safeArray(salesData.value.days).length > 0
  );
});

// Expand all rows function
const expandAll = () => {
  if (!salesData.value) return;

  getCitiesWithData().forEach((city) => {
    expandedCities[city] = true;

    getLocationsWithData(city).forEach((location) => {
      const locationKey = `${city}-${location}`;
      expandedLocations[locationKey] = true;

      getBrandsWithData(city, location).forEach((brand) => {
        const brandKey = `${city}-${location}-${brand}`;
        expandedBrands[brandKey] = true;
      });
    });
  });
};

// Collapse all rows function
const collapseAll = () => {
  // Clear all expanded states
  Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
  Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
  Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);
};

const formatDayForTable = (dateString) => {
  const date = new Date(dateString);
  // Format: "15 Jan (Mon)"
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${day} ${month} <br/><span style="font-size: 0.8rem; color: #666">${weekday}</span>`;
};

// Formatting functions
// const formatDay = (dateString) => {
//   const date = new Date(dateString);
//   return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
// };

// const hasSummaryData = computed(() => {
//   return (
//     salesData.value &&
//     (getPlatformsWithAnyData().length > 0 || getLocationsWithAnyData().length > 0)
//   );
// });

// const dateRangeSummary = computed(() => {
//   if (!salesData.value?.days || salesData.value.days.length === 0) return null;

//   const days = safeArray(salesData.value.days);
//   if (days.length === 1) {
//     return `Showing data for ${formatDay(days[0])}`;
//   } else {
//     return `Showing data from ${formatDay(days[0])} to ${formatDay(days[days.length - 1])} (${days.length} days)`;
//   }
// });

const numberOfDays = computed(() => {
  return safeArray(salesData.value?.days).length;
});

// Safe data access utilities
const safeArray = (arr) => {
  return Array.isArray(arr) ? arr : [];
};

const getSafeValue = (obj, path, defaultValue = 0) => {
  if (!obj) return defaultValue;
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

// Expected combinations lookup from business rules
const expectedLookup = computed(() => {
  const combos = salesData.value?.expectedCombinations || [];
  return {
    full: new Set(combos),
    cities: new Set(combos.map(c => c.split('|')[0])),
    cityLocations: new Set(combos.map(c => c.split('|').slice(0, 2).join('|'))),
    cityLocationBrands: new Set(combos.map(c => c.split('|').slice(0, 3).join('|'))),
  };
});

// Data filtering functions - show items with actual sales or expected by business rules
const getCitiesWithData = () => {
  if (!salesData.value?.cities) return [];
  return safeArray(salesData.value.cities).filter(
    (city) => getCityTotal(city) > 0 || expectedLookup.value.cities.has(city),
  );
};

const getLocationsWithData = (city) => {
  if (!salesData.value?.locations) return [];
  return safeArray(salesData.value.locations).filter(
    (location) => getLocationCityTotal(city, location) > 0 || expectedLookup.value.cityLocations.has(`${city}|${location}`),
  );
};

const getBrandsWithData = (city, location) => {
  if (!salesData.value?.brands) return [];
  return safeArray(salesData.value.brands).filter(
    (brand) => getBrandLocationCityTotal(city, location, brand) > 0 || expectedLookup.value.cityLocationBrands.has(`${city}|${location}|${brand}`),
  );
};

const getPlatformsWithData = (city, location, brand) => {
  if (!salesData.value?.platforms) return [];
  return safeArray(salesData.value.platforms).filter(
    (platform) => getPlatformTotalForBrand(city, location, brand, platform) > 0 || expectedLookup.value.full.has(`${city}|${location}|${brand}|${platform}`),
  );
};

const getPlatformsWithAnyData = () => {
  if (!salesData.value?.platforms) return [];
  return safeArray(salesData.value.platforms).filter((platform) => getPlatformTotal(platform) > 0);
};

const getLocationsWithAnyData = () => {
  if (!salesData.value?.locations) return [];
  return safeArray(salesData.value.locations).filter((location) => getLocationTotal(location) > 0);
};

// Expanded state functions
const isCityExpanded = (city) => {
  return expandedCities[city] === true;
};

const isLocationExpanded = (city, location) => {
  return expandedLocations[`${city}-${location}`] === true;
};

const isBrandExpanded = (city, location, brand) => {
  return expandedBrands[`${city}-${location}-${brand}`] === true;
};

const toggleCity = (city) => {
  expandedCities[city] = !expandedCities[city];
};

const toggleLocation = (city, location) => {
  const key = `${city}-${location}`;
  expandedLocations[key] = !expandedLocations[key];
};

const toggleBrand = (city, location, brand) => {
  const key = `${city}-${location}-${brand}`;
  expandedBrands[key] = !expandedBrands[key];
};

// const getWeekday = (dateString) => {
//   const date = new Date(dateString);
//   return date.toLocaleDateString("en-US", { weekday: "short" });
// };

// Average calculation functions
const getCityAverage = (city) => {
  const total = getCityTotal(city);
  return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
};

const getLocationCityAverage = (city, location) => {
  const total = getLocationCityTotal(city, location);
  return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
};

const getBrandLocationCityAverage = (city, location, brand) => {
  const total = getBrandLocationCityTotal(city, location, brand);
  return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
};

const getPlatformAverageForBrand = (city, location, brand, platform) => {
  const total = getPlatformTotalForBrand(city, location, brand, platform);
  return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
};

const getGrandAverage = () => {
  const total = getSafeValue(salesData.value, ["grandTotal"], 0);
  return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
};

// const getPlatformAverage = (platform) => {
//   const total = getPlatformTotal(platform);
//   return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
// };

// const getLocationAverage = (location) => {
//   const total = getLocationTotal(location);
//   return numberOfDays.value > 0 ? total / numberOfDays.value : 0;
// };

// Data access functions
const getLocationCityDayTotal = (city, location, day) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.brands).forEach((brand) => {
    safeArray(salesData.value.platforms).forEach((platform) => {
      total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
    });
  });
  return total;
};

const getLocationCityTotal = (city, location) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.brands).forEach((brand) => {
    safeArray(salesData.value.platforms).forEach((platform) => {
      safeArray(salesData.value.days).forEach((day) => {
        total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
      });
    });
  });
  return total;
};

const getBrandLocationCityDayTotal = (city, location, brand, day) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.platforms).forEach((platform) => {
    total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
  });
  return total;
};

const getBrandLocationCityTotal = (city, location, brand) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.platforms).forEach((platform) => {
    safeArray(salesData.value.days).forEach((day) => {
      total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
    });
  });
  return total;
};

const getPlatformData = (city, location, brand, platform, day) => {
  return getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
};

const getPlatformTotalForBrand = (city, location, brand, platform) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.days).forEach((day) => {
    total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
  });
  return total;
};

const getCityTotal = (city) => {
  const rowTotals = getSafeValue(salesData.value, ["rowTotals", city], {});
  return Object.values(rowTotals).reduce((sum, amount) => sum + amount, 0);
};

const getGrandDayTotal = (day) => {
  if (!salesData.value?.columnTotals) return 0;
  let total = 0;
  safeArray(salesData.value.brands).forEach((brand) => {
    total += getSafeValue(salesData.value.columnTotals, [brand, day], 0);
  });
  return total;
};

const getPlatformTotal = (platform) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.cities).forEach((city) => {
    safeArray(salesData.value.locations).forEach((location) => {
      safeArray(salesData.value.brands).forEach((brand) => {
        safeArray(salesData.value.days).forEach((day) => {
          total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
        });
      });
    });
  });
  return total;
};

const getLocationTotal = (location) => {
  if (!salesData.value?.data) return 0;
  let total = 0;
  safeArray(salesData.value.cities).forEach((city) => {
    safeArray(salesData.value.brands).forEach((brand) => {
      safeArray(salesData.value.platforms).forEach((platform) => {
        safeArray(salesData.value.days).forEach((day) => {
          total += getSafeValue(salesData.value.data, [city, location, brand, platform, day], 0);
        });
      });
    });
  });
  return total;
};

// Export functions with CSV
const exportToCSV = async () => {
  const dataToExport = flattenDailyData();

  if (!dataToExport || dataToExport.length === 0) {
    error.value = "No data available to export";
    return;
  }

  // Build confirmation message with filter info
  const filterInfo = hasActiveFilters.value
    ? `<p class="text-warning mb-2"><strong>Filters are active.</strong> Only filtered data will be exported.</p>`
    : "";

  const daysCount = salesData.value?.days?.length || 0;

  const confirmed = await confirm({
    title: "Export to CSV",
    type: "info",
    htmlMessage: `
      ${filterInfo}
      <p>The following data will be exported:</p>
      <ul class="mt-2">
        <li><strong>${dataToExport.length}</strong> rows</li>
        <li><strong>${daysCount}</strong> days</li>
        <li><strong>${totalsSummary.value?.amount ? formatCurrency(totalsSummary.value.amount) : 0}</strong> AED total</li>
      </ul>
    `,
    confirmText: "Export",
    cancelText: "Cancel",
  });

  if (!confirmed) return;

  exporting.value = true;
  try {
    generateCSVFile(dataToExport);
  } catch (err) {
    console.error("Export failed:", err);
    error.value = "Failed to export data";
  } finally {
    exporting.value = false;
  }
};

const flattenDailyData = () => {
  if (!salesData.value) return [];

  const flattenedData = [];
  const days = safeArray(salesData.value.days);

  const cities = getCitiesWithData();

  cities.forEach((city) => {
    const locations = getLocationsWithData(city);

    // If no locations, add city total
    if (locations.length === 0) {
      flattenedData.push({
        city: city,
        location: "",
        brand: "",
        platform: "",
        ...Object.fromEntries(
          days.map((day) => [day, getSafeValue(salesData.value.rowTotals, [city, day], 0)]),
        ),
        total: getCityTotal(city),
        average: getCityAverage(city),
      });
    } else {
      locations.forEach((location) => {
        const brands = getBrandsWithData(city, location);

        // If no brands, add location total
        if (brands.length === 0) {
          flattenedData.push({
            city: city,
            location: location,
            brand: "",
            platform: "",
            ...Object.fromEntries(
              days.map((day) => [day, getLocationCityDayTotal(city, location, day)]),
            ),
            total: getLocationCityTotal(city, location),
            average: getLocationCityAverage(city, location),
          });
        } else {
          brands.forEach((brand) => {
            const platforms = getPlatformsWithData(city, location, brand);

            // If no platforms, add brand total
            if (platforms.length === 0) {
              flattenedData.push({
                city: city,
                location: location,
                brand: brand,
                platform: "",
                ...Object.fromEntries(
                  days.map((day) => [
                    day,
                    getBrandLocationCityDayTotal(city, location, brand, day),
                  ]),
                ),
                total: getBrandLocationCityTotal(city, location, brand),
                average: getBrandLocationCityAverage(city, location, brand),
              });
            } else {
              platforms.forEach((platform) => {
                // Add platform detail row
                flattenedData.push({
                  city: city,
                  location: location,
                  brand: brand,
                  platform: platform,
                  ...Object.fromEntries(
                    days.map((day) => [day, getPlatformData(city, location, brand, platform, day)]),
                  ),
                  total: getPlatformTotalForBrand(city, location, brand, platform),
                  average: getPlatformAverageForBrand(city, location, brand, platform),
                });
              });
            }
          });
        }
      });
    }
  });

  return flattenedData;
};

const generateCSVFile = (data) => {
  if (!data.length) return;

  const days = safeArray(salesData.value.days);

  // Define headers - FLAT STRUCTURE with Average column
  const headers = [
    "City",
    "Location",
    "Brand",
    "Platform",
    ...days.map((day) => formatDayForCSV(day)),
    "Total",
    `Average per Day (${numberOfDays.value} days)`,
  ];

  // Convert data to CSV rows
  const csvRows = [
    headers.join(","),
    ...data.map((item) =>
      [
        escapeCSV(item.city),
        escapeCSV(item.location),
        escapeCSV(item.brand),
        escapeCSV(item.platform),
        ...days.map((day) => formatAmountForCSV(item[day] || 0)),
        formatAmountForCSV(item.total),
        formatAmountForCSV(item.average || 0),
      ].join(","),
    ),
  ];

  // Create CSV content
  const csvContent = csvRows.join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `daily_sales_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeCSV = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const formatDayForCSV = (dateString) => {
  const date = new Date(dateString);
  // Use YYYY-MM-DD format for better CSV compatibility
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatAmountForCSV = (amount) => {
  const number = typeof amount === "number" ? amount : parseFloat(amount) || 0;
  // Force comma as decimal separator for universal CSV compatibility
  return number.toFixed(2).replace(".", ",");
};

// Data fetching
const fetchDailyData = async () => {
  loading.value = true;
  error.value = "";
  try {
    const response = await api.accountingApi.get("/api/accounting/sales/daily", { params: activeFilters.value });
    salesData.value = response.data.data;
    filterTotals.value = response.data.totals;

    // Reset expanded states
    Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
    Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
    Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);

    // Auto-expand cities with data for better UX
    getCitiesWithData().forEach((city) => {
      expandedCities[city] = true;
    });
  } catch (err) {
    console.error("Failed to load daily sales matrix:", err);
    error.value = err.response?.data?.error || "Failed to load daily sales matrix";
  } finally {
    loading.value = false;
  }
};

// App bar actions
watchEffect(() => {
  setActions([
    {
      icon: "mdi-filter-variant",
      tooltip: "Filters",
      onClick: toggleFilterExpand,
      badge: numberOfActiveFilters.value > 0 ? { content: numberOfActiveFilters.value, show: true, color: "error" } : null,
    },
    {
      icon: "mdi-file-delimited-outline",
      tooltip: "Export CSV",
      onClick: exportToCSV,
      loading: exporting.value,
    },
    {
      icon: "mdi-chart-bar",
      tooltip: "Chart",
      onClick: () => router.push("/admin/charts/sales/daily"),
    },
  ]);
});
onBeforeUnmount(() => clearActions());
</script>

<style scoped>
.sticky-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  background: rgb(var(--v-theme-background));
}

/* Make ALL header cells sticky, not just first child */
.sticky-table :deep(thead tr th) {
  position: sticky;
  top: 0;
  z-index: 2;
  /* background: rgb(var(--v-theme-background)); */
}

/* First column remains sticky on the left */
.sticky-table :deep(thead tr th:first-child),
.sticky-table :deep(tbody tr td:first-child),
.sticky-table :deep(tfoot tr td:first-child) {
  position: sticky;
  left: 0;
  z-index: 3;
  background: rgb(var(--v-theme-background));
  min-width: 175px !important;
  width: 175px !important;
}

.sticky-table :deep(thead tr th:first-child) {
  z-index: 4; /* Higher than other headers */
}

.sticky-table :deep(tfoot tr td:first-child) {
  z-index: 3;
}

/* Remove or fix the last child sticky positioning if not needed */
/*
.sticky-table :deep(thead tr th:last-child) {
  z-index: 3;
}

.sticky-table :deep(tfoot tr td:last-child) {
  z-index: 3;
}
*/

/* Add vertical alignment for better appearance */
.sticky-table :deep(thead tr th) {
  vertical-align: middle;
}

/* Rest of your styles remain the same */
.sticky-table :deep(.city-header td:first-child .v-icon) {
  margin-left: 8px !important;
}

.sticky-table :deep(.city-header td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.location-total-row td:first-child .v-icon) {
  margin-left: 24px !important;
}

.sticky-table :deep(.location-total-row td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.brand-total-row td:first-child .v-icon) {
  margin-left: 40px !important;
}

.sticky-table :deep(.brand-total-row td:first-child strong) {
  margin-left: 4px !important;
}

.sticky-table :deep(.platform-row td:first-child .platform-indent) {
  margin-left: 46px !important;
}

.summary-card {
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.platform-summary,
.location-summary {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.platform-summary:last-child,
.location-summary:last-child {
  border-bottom: none;
}
</style>
